import React, { useEffect, useState } from "react";
import { XCircle, CheckCircle } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";

import "./ImageRequestsTable.css";
import {
  listImagesByCollaboration,
  updateImageStatus,
} from "../../../api/admin/images";
import { useAuth } from "../../../context/AuthContext";

type ImageStatusFront = "pending" | "approved" | "rejected";
interface ImageRequestRow {
  id: number;
  code: string;
  url: string;
  status: ImageStatusFront;
}

const ImageRequestsTable: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { getCsrfToken, user } = useAuth();
  const [requests, setRequests] = useState<ImageRequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<ImageRequestRow | null>(
    null
  );

  useEffect(() => {
    if (!requestId) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listImagesByCollaboration(
          Number(requestId),
          getCsrfToken()!,
          currentPage,
          itemsPerPage
        );
        if (mounted)
          setRequests(
            response.items.map((i: any) => ({
              id: i.imagem_id,
              code: i.codigo_imagem,
              url: i.url_imagem,
              status:
                i.status === "Aprovada" || i.status === "Aprovado"
                  ? "approved"
                  : i.status === "Rejeitada" || i.status === "Rejeitado"
                  ? "rejected"
                  : "pending",
            }))
          );
      } catch (e: any) {
        if (mounted) setError(e?.message || "Erro ao carregar imagens");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [requestId, getCsrfToken, currentPage, itemsPerPage]);

  const handleAddComment = () => {
    
  };

  const handleApprove = async (id: number) => {
    try {
      await updateImageStatus(id, "approved", getCsrfToken()!);
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: "approved" } : r))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleReject = async (id: number) => {
    try {
      await updateImageStatus(id, "rejected", getCsrfToken()!);
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: "rejected" } : r))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const totalPages = Math.ceil((requests.length || 1) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = requests.slice(startIndex, endIndex);

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-3">
            <Sidebar
              activeItem="imagens"
              avatar={avatar}
              name={user?.nome_completo || "Administrador"}
            />
          </div>
          <div className="col-9">
            <div className="content-area">
              <div className="page-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0">Informações</h5>
                </div>
                <Button className="btn-add-comment" onClick={handleAddComment}>
                  <span>+</span>
                  Adicionar comentário
                </Button>
              </div>

              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>CÓDIGO DA IMAGEM</th>
                      <th>IMAGEM</th>
                      <th>STATUS</th>
                      <th className="text-center">AÇÕES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={4}>Carregando...</td>
                      </tr>
                    )}
                    {error && !loading && (
                      <tr>
                        <td colSpan={4} className="text-danger">
                          {error}
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      !error &&
                      currentRequests.map((request) => (
                        <tr key={request.id}>
                          <td>{request.code}</td>
                          <td>
                            <img
                              src={request.url}
                              alt="Preview"
                              className="image-preview"
                              style={{ cursor: "pointer" }}
                              onClick={() => setPreviewImage(request)}
                            />
                          </td>
                          <td>
                            <span
                              className={`status-badge status-${request.status}`}
                            >
                              {request.status === "pending"
                                ? "Pendente"
                                : request.status === "approved"
                                ? "Aprovado"
                                : "Rejeitado"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon reject"
                                onClick={() => handleReject(request.id)}
                                title="Reprovar"
                              >
                                <XCircle size={20} />
                              </button>
                              <button
                                className="btn-icon approve"
                                onClick={() => handleApprove(request.id)}
                                title="Aprovar"
                              >
                                <CheckCircle size={20} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex mb-3 gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                >
                  Voltar
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/solicitacoes-colaboracao-admin")}
                >
                  Voltar para lista
                </button>
              </div>
              <div className="pagination-container">
                <div className="d-flex align-items-center gap-3">
                  <span className="pagination-info">
                    {startIndex + 1}-{Math.min(endIndex, requests.length)} de{" "}
                    {requests.length}
                  </span>
                  <div className="btn-group">
                    <button
                      className="btn"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                    >
                      ‹
                    </button>
                    <button
                      className="btn"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                    >
                      ›
                    </button>
                  </div>
                  <select
                    className="rows-per-page"
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value={10}>10 linhas</option>
                    <option value={20}>20 linhas</option>
                    <option value={50}>50 linhas</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {previewImage && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          tabIndex={-1}
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Imagem {previewImage.code}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewImage(null)}
                />
              </div>
              <div className="modal-body d-flex flex-column align-items-center">
                <img
                  src={previewImage.url}
                  alt={previewImage.code}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
                <div className="mt-3 d-flex gap-2">
                  <button
                    className="btn btn-outline-danger"
                    disabled={previewImage.status === "rejected"}
                    onClick={() => {
                      handleReject(previewImage.id);
                      setPreviewImage((p) =>
                        p ? { ...p, status: "rejected" } : p
                      );
                    }}
                  >
                    Reprovar
                  </button>
                  <button
                    className="btn btn-outline-success"
                    disabled={previewImage.status === "approved"}
                    onClick={() => {
                      handleApprove(previewImage.id);
                      setPreviewImage((p) =>
                        p ? { ...p, status: "approved" } : p
                      );
                    }}
                  >
                    Aprovar
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPreviewImage(null)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageRequestsTable;
